import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { TrendingUp, Package, Warehouse, DollarSign, FileText, MapPin, RotateCcw, BarChart3, Search, ExternalLink } from "lucide-react";
import { fmtDate as fmtIstDate } from "@/lib/dateFormat";

const p2r = (p) => (p || 0) / 100;
const fmtR = (paise) => { const r = p2r(paise); if (r >= 10000000) return `₹${(r/10000000).toFixed(2)} Cr`; if (r >= 100000) return `₹${(r/100000).toFixed(2)} L`; if (r >= 1000) return `₹${(r/1000).toFixed(1)} K`; return `₹${r.toLocaleString("en-IN")}`; };
const fN = (n) => (n || 0).toLocaleString("en-IN");
const PL = { blinkit: "Blinkit", zepto: "Zepto", swiggy: "Swiggy Instamart", bigbasket: "BigBasket", jiomart: "JioMart" };
const fmtDate = (d) => { if (!d) return "—"; const s = String(d).trim(); if (!s || s === "—") return "—"; try { const dt = new Date(s); if (isNaN(dt.getTime())) return s.slice(0,10); return fmtIstDate(dt.toISOString()); } catch { return s.slice(0,10); } };

const T = {
  bg: "#060a10", surface: "#0c1220", surface2: "#111a2e", border: "#1a2744",
  text: "#e8edf5", dim: "#7a8baa", muted: "#4a5a7a",
  green: "#00e676", red: "#ff3d57", amber: "#ffab00", blue: "#448aff", cyan: "#00e5ff",
};

const PlatformBadge = ({ platform }) => {
  const colors = { blinkit: ["rgba(245,214,35,0.15)", "#f5d623"], zepto: ["rgba(139,92,246,0.15)", "#8b5cf6"], swiggy: ["rgba(252,128,25,0.15)", "#fc8019"] };
  const [bg, fg] = colors[platform] || ["rgba(255,255,255,0.08)", T.dim];
  return <span style={{ background: bg, color: fg, padding: "2px 8px", borderRadius: "4px", fontSize: "10px", fontWeight: 600 }}>{PL[platform] || platform}</span>;
};

const StatusBadge = ({ status, type }) => {
  const m = {
    green: [`rgba(0,230,118,0.12)`, T.green], red: [`rgba(255,61,87,0.12)`, T.red],
    amber: [`rgba(255,171,0,0.12)`, T.amber], blue: [`rgba(68,138,255,0.12)`, T.blue],
  };
  const [bg, fg] = m[type] || m.blue;
  return <span style={{ background: bg, color: fg, padding: "2px 8px", borderRadius: "4px", fontSize: "10px", fontWeight: 600 }}>{status}</span>;
};

const Card = ({ label, value, color, sub }) => (
  <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: "12px", padding: "16px 20px" }}>
    <div style={{ fontSize: "10px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "1.5px", color: T.muted, marginBottom: "8px" }}>{label}</div>
    <div style={{ fontSize: "28px", fontWeight: 700, fontFamily: "monospace", color: color || T.green }}>{value}</div>
    {sub && <div style={{ fontSize: "11px", color: T.dim, marginTop: "4px" }}>{sub}</div>}
  </div>
);

const SectionHeader = ({ icon: Icon, title }) => (
  <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "16px", marginTop: "32px" }}>
    <div style={{ width: "32px", height: "32px", borderRadius: "8px", background: "rgba(0,230,118,0.08)", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Icon size={16} style={{ color: T.green }} />
    </div>
    <h2 style={{ fontSize: "16px", fontWeight: 700, color: T.text, letterSpacing: "-0.3px" }}>{title}</h2>
  </div>
);

const TH = ({ children, right }) => <th style={{ padding: "10px 12px", fontSize: "10px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "1px", color: T.muted, textAlign: right ? "right" : "left", background: "rgba(0,0,0,0.25)", borderBottom: `1px solid ${T.border}` }}>{children}</th>;
const TD = ({ children, right, mono, color }) => <td style={{ padding: "8px 12px", fontSize: "12px", color: color || T.text, textAlign: right ? "right" : "left", fontFamily: mono ? "monospace" : "inherit", borderBottom: `1px solid ${T.border}` }}>{children}</td>;

export default function PulseAnalyticsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pf, setPf] = useState("");
  const [platforms, setPlatforms] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [offtake, setOfftake] = useState(null);
  const [po, setPo] = useState(null);
  const [poDetails, setPoDetails] = useState(null);
  const [stock, setStock] = useState(null);
  const [pnl, setPnl] = useState(null);
  const [recv, setRecv] = useState(null);
  const [ded, setDed] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [margins, setMargins] = useState(null);
  const [sellThrough, setSellThrough] = useState(null);

  const roles = user?.roles || [];
  const canPnl = roles.some(r => ["Admin", "admin", "Finance Manager", "Finance Executive"].includes(r));

  useEffect(() => {
    if (!user) { navigate("/login"); return; }
    const orig = document.body.style.background;
    document.body.style.background = T.bg;
    return () => { document.body.style.background = orig; };
  }, [user, navigate]);

  useEffect(() => {
    api.get("/pulse/platforms").then(r => setPlatforms(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const params = {}; if (pf) params.platform = pf;
      try {
        const [o, p, pd, s, r, d, c, m, st] = await Promise.allSettled([
          api.get("/pulse/offtake/summary", { params }),
          api.get("/pulse/po/summary", { params }),
          api.get("/pulse/po/details", { params }),
          api.get("/pulse/stock/summary", { params }),
          api.get("/pulse/receivables", { params }),
          api.get("/pulse/deductions/summary", { params }),
          api.get("/pulse/coverage", { params }),
          api.get("/pulse/sku-margins", { params }),
          api.get("/pulse/offtake/sell-through", { params }),
        ]);
        if (o.status === "fulfilled") setOfftake(o.value.data);
        if (p.status === "fulfilled") setPo(p.value.data);
        if (pd.status === "fulfilled") setPoDetails(pd.value.data);
        if (s.status === "fulfilled") setStock(s.value.data);
        if (r.status === "fulfilled") setRecv(r.value.data);
        if (d.status === "fulfilled") setDed(d.value.data);
        if (c.status === "fulfilled") setCoverage(c.value.data);
        if (m.status === "fulfilled") setMargins(m.value.data);
        if (st.status === "fulfilled") setSellThrough(st.value.data);
        if (canPnl) {
          try { const pnlR = await api.get("/pulse/pnl/summary", { params }); setPnl(pnlR.data); } catch {}
        }
      } catch (e) { console.error("Analytics load error:", e); }
      setLoading(false);
    };
    if (user) load();
  }, [pf, user]);

  const matchSearch = (obj) => {
    if (!search) return true;
    return JSON.stringify(obj).toLowerCase().includes(search.toLowerCase());
  };

  if (!user) return null;

  const tableStyle = { width: "100%", borderCollapse: "collapse", background: T.surface, borderRadius: "8px", overflow: "hidden", border: `1px solid ${T.border}` };
  const rowHover = { transition: "background 0.15s" };

  return (
    <div style={{ minHeight: "100vh", background: T.bg, color: T.text, fontFamily: "'Outfit', sans-serif" }} data-testid="pulse-analytics-page">
      <style>{`
        @keyframes heartbeat {
          0%,100% { transform:scale(1); }
          15% { transform:scale(1.3); }
          30% { transform:scale(1); }
          45% { transform:scale(1.15); }
          60% { transform:scale(1); }
        }
        .da-row:hover { background: #162038 !important; }
      `}</style>

      {/* TOPBAR */}
      <div style={{ background: T.surface, borderBottom: `1px solid ${T.border}`, padding: "14px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 50 }} data-testid="analytics-topbar">
        <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
          <div>
            <div style={{ fontSize: "11px", fontWeight: 600, letterSpacing: "4px", color: T.dim, textTransform: "uppercase" }}>ORBIT</div>
            <div style={{ fontSize: "24px", fontWeight: 700, fontFamily: "monospace", background: `linear-gradient(135deg, ${T.green}, ${T.cyan})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>PULSE</div>
          </div>
          <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: T.green, boxShadow: `0 0 12px rgba(0,230,118,0.4)`, animation: "heartbeat 1.5s ease-in-out infinite" }} />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div style={{ position: "relative" }}>
            <Search size={12} style={{ position: "absolute", left: "8px", top: "50%", transform: "translateY(-50%)", color: T.muted }} />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search all tables..." style={{ height: "30px", paddingLeft: "28px", paddingRight: "10px", fontSize: "11px", background: T.surface2, border: `1px solid ${T.border}`, borderRadius: "6px", color: T.text, width: "200px", outline: "none" }} data-testid="analytics-search" />
          </div>
          <select value={pf} onChange={e => setPf(e.target.value)} style={{ height: "30px", fontSize: "11px", background: T.surface2, border: `1px solid ${T.border}`, borderRadius: "6px", color: T.text, padding: "0 8px", outline: "none" }} data-testid="analytics-platform-filter">
            <option value="">All Platforms</option>
            {platforms.map(p => <option key={p} value={p}>{PL[p] || p}</option>)}
          </select>
          <span style={{ fontSize: "11px", color: T.muted, fontFamily: "monospace" }}>SKIPPI ICE POPS PVT LTD</span>
          <a href="/pulse" style={{ fontSize: "12px", color: T.dim, textDecoration: "none" }} data-testid="back-to-orbit-link">← Back to ORBIT</a>
        </div>
      </div>

      {loading && <div style={{ textAlign: "center", padding: "80px 0", color: T.dim, fontSize: "14px" }}>Loading analytics...</div>}

      {!loading && (
        <div style={{ maxWidth: "1440px", margin: "0 auto", padding: "24px" }}>

          {/* OVERVIEW VITALS */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: "16px", marginBottom: "8px" }}>
            <Card label="Total GMV" value={fmtR(offtake?.total_gmv || 0)} color={T.green} />
            <Card label="Total Units Sold" value={fN(offtake?.total_qty || 0)} color={T.cyan} />
            <Card label="PO Fill Rate" value={`${po?.fill_rate || 0}%`} color={(po?.fill_rate || 0) >= 80 ? T.green : (po?.fill_rate || 0) >= 50 ? T.amber : T.red} />
            <Card label="Stock Discrepancy" value={`${fN(stock?.total_discrepancy || 0)} units`} color={(stock?.total_discrepancy || 0) > 0 ? T.red : T.green} />
            <Card label="Outstanding" value={fmtR(recv?.total_outstanding || 0)} color={T.red} />
          </div>

          {/* OFFTAKE */}
          {offtake && offtake.total_qty > 0 && (<>
            <SectionHeader icon={TrendingUp} title="Offtake Analytics" />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
              <div>
                <div style={{ fontSize: "11px", fontWeight: 600, color: T.dim, marginBottom: "8px", textTransform: "uppercase", letterSpacing: "1px" }}>Top Cities by GMV</div>
                <div style={{ borderRadius: "8px", overflow: "hidden" }}>
                  <table style={tableStyle}><thead><tr><TH>#</TH><TH>City</TH><TH>Platform</TH><TH right>Units</TH><TH right>GMV</TH></tr></thead>
                  <tbody>{(offtake.by_city || []).filter(matchSearch).slice(0, 15).map((c, i) => (
                    <tr key={i} className="da-row"><TD>{i+1}</TD><TD>{c.city || "—"}</TD><TD><PlatformBadge platform={c.platform} /></TD><TD right mono>{fN(c.qty)}</TD><TD right mono color={T.green}>{fmtR(c.gmv)}</TD></tr>
                  ))}</tbody></table>
                </div>
              </div>
              <div>
                <div style={{ fontSize: "11px", fontWeight: 600, color: T.dim, marginBottom: "8px", textTransform: "uppercase", letterSpacing: "1px" }}>SKU Performance</div>
                <div style={{ borderRadius: "8px", overflow: "hidden" }}>
                  <table style={tableStyle}><thead><tr><TH>SKU</TH><TH>Platform</TH><TH right>Units</TH><TH right>GMV</TH></tr></thead>
                  <tbody>{(offtake.by_sku || []).filter(matchSearch).slice(0, 15).map((s, i) => (
                    <tr key={i} className="da-row"><TD>{(s.item_name || "").substring(0, 45)}</TD><TD><PlatformBadge platform={s.platform} /></TD><TD right mono>{fN(s.qty)}</TD><TD right mono color={T.green}>{fmtR(s.gmv)}</TD></tr>
                  ))}</tbody></table>
                </div>
              </div>
            </div>
            {sellThrough && sellThrough.total_primary > 0 && (
              <div style={{ marginTop: "16px", display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "16px" }}>
                <Card label="Primary (Shipped)" value={fN(sellThrough.total_primary)} color={T.cyan} />
                <Card label="Secondary (Sold)" value={fN(sellThrough.total_secondary)} color={T.green} />
                <Card label="Sell-Through Rate" value={`${sellThrough.sell_through_rate}%`} color={sellThrough.sell_through_rate > 80 ? T.green : sellThrough.sell_through_rate > 60 ? T.amber : T.red} />
              </div>
            )}
          </>)}

          {/* PO FULFILMENT */}
          {po && po.total_ordered > 0 && (<>
            <SectionHeader icon={Package} title="PO Fulfilment" />
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "16px", marginBottom: "16px" }}>
              <Card label="Fully Serviced" value={fN(poDetails?.fully_serviced || 0)} color={T.green} />
              <Card label="Partially Serviced" value={fN(poDetails?.partially_serviced || 0)} color={T.amber} />
              <Card label="Zero Fill" value={fN(poDetails?.zero_fill || 0)} color={T.red} />
              <Card label="Pending" value={fN(poDetails?.pending || 0)} color={T.blue} />
            </div>
            <div style={{ borderRadius: "8px", overflow: "hidden" }}>
              <table style={tableStyle}><thead><tr><TH>City</TH><TH right>Ordered</TH><TH right>Received</TH><TH right>Remaining</TH><TH right>Fill Rate</TH><TH>Status</TH></tr></thead>
              <tbody>{[...(po.by_city || [])].sort((a,b) => a.fill_rate - b.fill_rate).filter(matchSearch).slice(0, 20).map((c, i) => {
                const st = c.fill_rate >= 90 ? ["GOOD", "green"] : c.fill_rate >= 50 ? ["WEAK", "amber"] : c.fill_rate > 0 ? ["CRITICAL", "red"] : ["DEAD", "red"];
                return <tr key={i} className="da-row"><TD>{c.city || "Unknown"}</TD><TD right mono>{fN(c.ordered)}</TD><TD right mono>{fN(c.received)}</TD><TD right mono>{fN(c.ordered - c.received)}</TD><TD right mono>{c.fill_rate}%</TD><TD><StatusBadge status={st[0]} type={st[1]} /></TD></tr>;
              })}</tbody></table>
            </div>
          </>)}

          {/* STOCK HEALTH */}
          {stock && (stock.total_backend > 0 || stock.total_frontend > 0) && (<>
            <SectionHeader icon={Warehouse} title="Stock Health" />
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "16px", marginBottom: "16px" }}>
              <Card label="Backend Stock" value={fN(stock.total_backend)} color={T.cyan} />
              <Card label="Frontend (Visible)" value={fN(stock.total_frontend)} color={T.green} />
              <Card label="Hidden / Discrepancy" value={`${fN(stock.total_discrepancy)} units`} color={stock.total_discrepancy > 0 ? T.red : T.green} />
              <Card label="Potential GMV Loss" value={fmtR(stock.total_potential_loss)} color={T.red} />
            </div>
            {stock.total_discrepancy > 1000 && (
              <div style={{ background: "linear-gradient(135deg, rgba(255,61,87,0.06), rgba(255,171,0,0.03))", border: `1px solid rgba(255,61,87,0.2)`, borderRadius: "8px", padding: "12px 16px", marginBottom: "16px", fontSize: "12px", color: T.red }}>
                <strong>{fN(stock.total_discrepancy)} units</strong> in backend inventory are invisible to customers. Escalate to platform ops team.
              </div>
            )}
            <div style={{ borderRadius: "8px", overflow: "hidden" }}>
              <table style={tableStyle}><thead><tr><TH>Facility</TH><TH right>Backend</TH><TH right>Frontend</TH><TH right>Hidden</TH><TH right>Potential Loss</TH><TH>Severity</TH></tr></thead>
              <tbody>{[...(stock.by_facility || [])].sort((a,b) => b.discrepancy - a.discrepancy).filter(matchSearch).slice(0, 10).map((f, i) => {
                const h = f.discrepancy;
                const sev = f.backend === 0 && f.frontend === 0 && f.potential_loss > 0 ? ["OOS","red"] : h > 1000 ? ["CRITICAL","red"] : h > 500 ? ["HIGH","amber"] : h > 100 ? ["MEDIUM","amber"] : h > 0 ? ["LOW","blue"] : ["OK","green"];
                return <tr key={i} className="da-row"><TD>{f.facility_name}</TD><TD right mono>{fN(f.backend)}</TD><TD right mono>{fN(f.frontend)}</TD><TD right mono>{fN(h)}</TD><TD right mono color={T.red}>{fmtR(f.potential_loss)}</TD><TD><StatusBadge status={sev[0]} type={sev[1]} /></TD></tr>;
              })}</tbody></table>
            </div>
          </>)}

          {/* P&L WATERFALL */}
          {canPnl && pnl && pnl.total_invoice > 0 && (<>
            <SectionHeader icon={DollarSign} title="P&L Reconciliation" />
            <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: "12px", padding: "24px" }}>
              {[
                ["Gross Invoice Amount", pnl.total_invoice, T.green, false],
                ["GRN Amount", pnl.total_grn, T.green, false],
                ["Debit Note Deductions", pnl.total_dn, T.red, true],
                ["TDS Deducted", pnl.total_tds, T.red, true],
                ["Purchase Returns (PRN)", pnl.total_prn, T.red, true],
                ["Net Approved Amount", pnl.total_approved, T.green, false],
                ["Amount Payable", pnl.total_payable, T.blue, false],
                ["Actually Paid", pnl.total_paid, T.green, false],
              ].map(([label, val, color, neg], i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: `1px solid ${T.border}` }}>
                  <span style={{ fontSize: "13px", color: T.dim }}>{label}</span>
                  <span style={{ fontFamily: "monospace", fontWeight: 600, fontSize: "14px", color }}>{neg ? "– " : ""}{fmtR(val)}</span>
                </div>
              ))}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 0", marginTop: "8px", borderTop: `2px solid ${T.muted}` }}>
                <span style={{ fontSize: "15px", fontWeight: 700, color: T.text }}>Outstanding / In Pipeline</span>
                <span style={{ fontFamily: "monospace", fontSize: "22px", fontWeight: 700, color: T.red }}>{fmtR(pnl.total_outstanding)}</span>
              </div>
            </div>
          </>)}

          {/* RECEIVABLES */}
          {recv && recv.total_invoices > 0 && (<>
            <SectionHeader icon={FileText} title="Receivables Summary" />
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "16px", marginBottom: "16px" }}>
              <Card label="Total Invoices" value={fN(recv.total_invoices)} color={T.cyan} />
              <Card label="Invoice Amount" value={fmtR(recv.total_invoice_amount)} color={T.text} />
              <Card label="Total Paid" value={fmtR(recv.total_paid)} color={T.green} />
              <Card label="Outstanding" value={fmtR(recv.total_outstanding)} color={T.red} />
            </div>
            <div style={{ borderRadius: "8px", overflow: "hidden" }}>
              <table style={tableStyle}><thead><tr><TH>Invoice ID</TH><TH>City</TH><TH right>Invoice Amt</TH><TH right>Paid</TH><TH right>Outstanding</TH><TH>Status</TH><TH>Date</TH></tr></thead>
              <tbody>{(recv.invoices || []).filter(i => !i.is_settled).filter(matchSearch).slice(0, 10).map((inv, i) => {
                const st = (inv.due_status||"").toLowerCase().includes("overdue") ? ["OVERDUE","red"] : inv.outstanding > 0 ? ["PENDING","amber"] : ["PAID","green"];
                return <tr key={i} className="da-row"><TD mono>{inv.invoice_id}</TD><TD>{inv.city}</TD><TD right mono>{fmtR(inv.invoice_amount)}</TD><TD right mono color={T.green}>{fmtR(inv.paid_amount)}</TD><TD right mono color={T.red}>{fmtR(inv.outstanding)}</TD><TD><StatusBadge status={st[0]} type={st[1]} /></TD><TD>{fmtDate(inv.invoice_date)}</TD></tr>;
              })}</tbody></table>
            </div>
          </>)}

          {/* DN / RETURNS */}
          {ded && ded.total_amount > 0 && (<>
            <SectionHeader icon={RotateCcw} title="DN / Returns" />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
              <div>
                <div style={{ fontSize: "11px", fontWeight: 600, color: T.dim, marginBottom: "12px", textTransform: "uppercase", letterSpacing: "1px" }}>DN/PRN Reasons</div>
                <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: "12px", padding: "16px" }}>
                  {(ded.by_reason || []).filter(matchSearch).slice(0, 10).map((r, i) => {
                    const maxAmt = ((ded.by_reason || [])[0]?.amount || 1);
                    const pct = Math.min(r.amount / maxAmt * 100, 100);
                    const bar = /damage/i.test(r.remark) ? T.red : /shortage|short/i.test(r.remark) ? T.amber : /excess/i.test(r.remark) ? T.amber : T.muted;
                    return (
                      <div key={i} style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px", fontSize: "12px" }}>
                        <span style={{ width: "120px", color: T.dim, flexShrink: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.remark || "Unknown"}</span>
                        <div style={{ flex: 1, height: "10px", background: "rgba(255,255,255,0.04)", borderRadius: "5px", overflow: "hidden" }}>
                          <div style={{ height: "100%", width: `${pct}%`, background: bar, borderRadius: "5px" }} />
                        </div>
                        <span style={{ fontFamily: "monospace", color: T.dim, flexShrink: 0, width: "100px", textAlign: "right" }}>{fN(r.count)} · {fmtR(r.amount)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div>
                <div style={{ fontSize: "11px", fontWeight: 600, color: T.dim, marginBottom: "12px", textTransform: "uppercase", letterSpacing: "1px" }}>Top Facilities by Deductions</div>
                <div style={{ borderRadius: "8px", overflow: "hidden" }}>
                  <table style={tableStyle}><thead><tr><TH>Facility</TH><TH right>Count</TH><TH right>Amount</TH></tr></thead>
                  <tbody>{[...(ded.by_facility || [])].sort((a,b) => b.amount - a.amount).filter(matchSearch).slice(0, 10).map((f, i) => (
                    <tr key={i} className="da-row"><TD>{f.facility_name}</TD><TD right mono>{fN(f.count)}</TD><TD right mono color={T.red}>{fmtR(f.amount)}</TD></tr>
                  ))}</tbody></table>
                </div>
              </div>
            </div>
          </>)}

          {/* CITY COVERAGE */}
          {coverage && coverage.cities?.length > 0 && (<>
            <SectionHeader icon={MapPin} title="City Coverage" />
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "16px", marginBottom: "16px" }}>
              <Card label="Active Cities" value={fN(coverage.total_active_cities)} color={T.green} />
              <Card label="Stocked Only" value={fN(coverage.total_stocked_cities)} color={T.blue} />
              <Card label="PO Only" value={fN(coverage.total_po_cities)} color={T.amber} />
              <Card label="Total Coverage" value={fN((coverage.total_active_cities||0)+(coverage.total_stocked_cities||0)+(coverage.total_po_cities||0))} color={T.cyan} />
            </div>
            <div style={{ borderRadius: "8px", overflow: "hidden" }}>
              <table style={tableStyle}><thead><tr><TH>City</TH><TH>Platforms</TH><TH>Sales</TH><TH>Stock</TH><TH>PO</TH><TH right>Total GMV</TH><TH>Status</TH></tr></thead>
              <tbody>{(coverage.cities || []).filter(matchSearch).slice(0, 20).map((c, i) => {
                const st = c.status === "active" ? ["ACTIVE","green"] : c.status === "stocked" ? ["STOCKED","blue"] : ["PO ONLY","amber"];
                return <tr key={i} className="da-row"><TD>{c.city}</TD><TD>{(c.platforms || []).map(p => <PlatformBadge key={p} platform={p} />)}</TD><TD>{c.has_sales ? <span style={{color:T.green}}>✓</span> : <span style={{color:T.muted}}>—</span>}</TD><TD>{c.has_stock ? <span style={{color:T.green}}>✓</span> : <span style={{color:T.muted}}>—</span>}</TD><TD>{c.has_po ? <span style={{color:T.green}}>✓</span> : <span style={{color:T.muted}}>—</span>}</TD><TD right mono color={T.green}>{fmtR(c.total_gmv)}</TD><TD><StatusBadge status={st[0]} type={st[1]} /></TD></tr>;
              })}</tbody></table>
            </div>
          </>)}

          {/* SKU MARGINS */}
          {margins && margins.skus?.length > 0 && (<>
            <SectionHeader icon={BarChart3} title="SKU Margins" />
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "16px", marginBottom: "16px" }}>
              <Card label="SKUs Tracked" value={fN(margins.skus?.length || 0)} color={T.cyan} />
              <Card label="Avg Platform Take" value={`${margins.avg_platform_take}%`} color={margins.avg_platform_take <= 35 ? T.green : T.amber} />
              <Card label="Landing Revenue" value={fmtR(margins.total_landing_revenue)} color={T.green} />
              <Card label="Platform Take" value={fmtR(margins.total_platform_take)} color={T.amber} />
            </div>
            <div style={{ borderRadius: "8px", overflow: "hidden" }}>
              <table style={tableStyle}><thead><tr><TH>SKU</TH><TH right>MRP</TH><TH right>Landing</TH><TH right>Take ₹</TH><TH right>Take %</TH><TH right>Units</TH><TH right>GMV</TH><TH>Status</TH></tr></thead>
              <tbody>{(margins.skus || []).filter(matchSearch).slice(0, 20).map((s, i) => {
                const st = s.margin_status === "HEALTHY" ? "green" : s.margin_status === "WATCH" ? "amber" : "red";
                return <tr key={i} className="da-row"><TD>{(s.item_name || "").substring(0, 45)}{s.landing_is_estimated && <span style={{color:T.muted,fontSize:"9px",marginLeft:"4px"}}>*est</span>}</TD><TD right mono>₹{s.mrp}</TD><TD right mono>₹{s.landing_price}</TD><TD right mono>₹{s.platform_take}</TD><TD right mono>{s.platform_take_pct}%</TD><TD right mono>{fN(s.total_units_sold)}</TD><TD right mono color={T.green}>{fmtR(s.total_gmv)}</TD><TD><StatusBadge status={s.margin_status} type={st} /></TD></tr>;
              })}</tbody></table>
            </div>
          </>)}

          <div style={{ textAlign: "center", padding: "40px 0 24px", color: T.muted, fontSize: "11px", fontFamily: "monospace" }}>
            ORBIT PULSE · Q-Commerce Health Monitor · {fmtIstDate(new Date().toISOString())}
          </div>
        </div>
      )}
    </div>
  );
}
